import { Link } from "react-router-dom";
import { AlertTriangle, Info } from "lucide-react";
import type { PatientDuplicateWarning } from "../../../shared/types/patient";
import { duplicateConfidenceLabel, formatDate, formatPhone } from "../../../shared/format";
import { Button } from "@/components/ui/button";

interface Props {
  warning: PatientDuplicateWarning;
  onAcceptAndContinue?: () => void;
  busy?: boolean;
}

export function DuplicateWarningCard({ warning, onAcceptAndContinue, busy }: Props) {
  const strong = warning.confidence === "STRONG";
  return (
    <div
      className={
        strong
          ? "rounded-md border border-destructive/40 bg-destructive/5 p-4"
          : "rounded-md border border-amber-300 bg-amber-50 p-4"
      }
    >
      <div
        className={
          strong
            ? "flex items-center gap-2 text-sm font-semibold text-destructive"
            : "flex items-center gap-2 text-sm font-semibold text-amber-700"
        }
      >
        {strong ? (
          <AlertTriangle className="size-4 shrink-0" aria-hidden="true" />
        ) : (
          <Info className="size-4 shrink-0" aria-hidden="true" />
        )}
        <span>
          {duplicateConfidenceLabel[warning.confidence]} — điểm khớp {warning.score.toFixed(3)}
        </span>
      </div>
      <p className="mt-2 text-[13px] text-foreground">
        Hệ thống phát hiện {warning.candidates.length} bệnh nhân có dấu hiệu trùng. Vui lòng kiểm tra
        trước khi tạo mới để tránh hồ sơ trùng lặp.
      </p>
      {warning.reasons.length > 0 && (
        <p className="mt-2 text-xs text-muted-foreground">Lý do: {warning.reasons.join(" · ")}</p>
      )}
      <div className="mt-2 space-y-1.5">
        {warning.candidates.map((c) => (
          <div
            key={c.patientId}
            className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm"
          >
            <span>
              <strong className="font-semibold text-foreground">{c.fullName}</strong>
              {c.patientCode && <span className="font-mono text-muted-foreground"> · {c.patientCode}</span>}
              {c.phoneE164 && <> · {formatPhone(c.phoneE164)}</>}
              {c.dateOfBirth && <> · sinh {formatDate(c.dateOfBirth)}</>}
            </span>
            <Link
              to={`/patients/${c.patientId}`}
              className="text-primary underline-offset-4 hover:underline"
            >
              Xem hồ sơ
            </Link>
          </div>
        ))}
      </div>
      {onAcceptAndContinue && (
        <div className="mt-3 flex justify-end gap-2">
          <Button
            type="button"
            variant={strong ? "destructive" : "secondary"}
            size="sm"
            onClick={onAcceptAndContinue}
            disabled={busy}
          >
            {strong ? "Vẫn tạo mới (cần xác nhận)" : "Vẫn tạo mới"}
          </Button>
        </div>
      )}
    </div>
  );
}
